// Signs people in with the realm's public client, the code flow with PKCE and
// no secret (D8), and keeps the API client's token current.
import { WebStorageStateStore } from 'oidc-client-ts';
import type { ReactNode } from 'react';
import { useEffect, useMemo } from 'react';
import { AuthProvider, useAuth } from 'react-oidc-context';

import { setTokenSource } from '../api/client';
import type { ClientConfigResponse } from '../api/generated/ClientConfigResponse';
import type { ReturnState, Session } from './session';
import { SessionContext, rolesOf } from './session';

const CALLBACK_PATH = '/auth/callback';

export function SessionProvider({ config, children }: { config: ClientConfigResponse; children: ReactNode }) {
    const origin = window.location.origin;
    return (
        <AuthProvider
            authority={config.authority}
            client_id={config.clientId}
            redirect_uri={`${origin}${CALLBACK_PATH}`}
            post_logout_redirect_uri={`${origin}/`}
            response_type="code"
            scope="openid profile"
            automaticSilentRenew
            userStore={new WebStorageStateStore({ store: window.sessionStorage })}
            // Clears the code and state from the address once they are used. The
            // callback page then goes back to where the sign-in started (AuthCallback).
            onSigninCallback={() => {
                window.history.replaceState({}, document.title, CALLBACK_PATH);
            }}
        >
            <SessionBridge>{children}</SessionBridge>
        </AuthProvider>
    );
}

function SessionBridge({ children }: { children: ReactNode }) {
    const auth = useAuth();
    const accessToken = auth.user?.expired === false ? auth.user.access_token : undefined;
    useEffect(() => {
        setTokenSource(() => accessToken);
    }, [accessToken]);
    const session = useMemo<Session>(() => {
        const profile = auth.user?.profile;
        return {
            loading: auth.isLoading,
            signedIn: accessToken !== undefined,
            roles: rolesOf(accessToken),
            ...(accessToken === undefined ? {} : { accessToken }),
            ...(profile?.preferred_username === undefined ? {} : { username: profile.preferred_username }),
            ...(profile?.name === undefined ? {} : { displayName: profile.name }),
            signIn: () => {
                const state: ReturnState = { returnTo: window.location.pathname + window.location.search };
                void auth.signinRedirect({ state });
            },
            signOut: () => {
                void auth.signoutRedirect();
            },
        };
    }, [auth, accessToken]);
    return <SessionContext value={session}>{children}</SessionContext>;
}
